import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePrompts } from '../contexts/PromptContext';
import { PROMPT_CATEGORIES } from '../utils/constants';
import { uploadApi } from '../services/api';
import toast from 'react-hot-toast';

interface AddPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ImagePreview {
  file: File;
  preview: string;
  uploading?: boolean;
  uploaded?: boolean;
  url?: string;
  error?: string;
}

const AddPromptModal: React.FC<AddPromptModalProps> = ({ isOpen, onClose }) => {
  const { addPrompt } = usePrompts();
  const [isLoading, setIsLoading] = useState(false);
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_IMAGES = 9;
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  // 处理文件（用于选择和拖拽）
  const processFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    const remainingSlots = MAX_IMAGES - images.length;
    if (remainingSlots <= 0) {
      toast.error(`最多只能上传${MAX_IMAGES}张图片`);
      return;
    }

    const newImages: ImagePreview[] = [];
    const errors: string[] = [];

    fileArray.slice(0, remainingSlots).forEach(file => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: 不支持的图片格式`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: 文件大小超过5MB`);
        return;
      }
      const preview = URL.createObjectURL(file);
      newImages.push({ file, preview });
    });

    if (errors.length > 0) {
      toast.error(errors.join('\n'));
    }

    if (newImages.length > 0) {
      setImages(prev => [...prev, ...newImages]);
    }
  };

  // 处理拖拽事件
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  };

  // 处理图片选择
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = MAX_IMAGES - images.length;
    if (remainingSlots <= 0) {
      toast.error(`最多只能上传${MAX_IMAGES}张图片`);
      return;
    }

    const newImages: ImagePreview[] = [];
    const errors: string[] = [];

    Array.from(files).slice(0, remainingSlots).forEach(file => {
      // 验证文件类型
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: 不支持的图片格式`);
        return;
      }

      // 验证文件大小
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: 文件大小超过5MB`);
        return;
      }

      // 创建预览
      const preview = URL.createObjectURL(file);
      newImages.push({ file, preview });
    });

    if (errors.length > 0) {
      toast.error(errors.join('\n'));
    }

    if (newImages.length > 0) {
      setImages(prev => [...prev, ...newImages]);
    }

    // 清空input以允许重复选择相同文件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 移除图片
  const handleRemoveImage = (index: number) => {
    setImages(prev => {
      const newImages = [...prev];
      // 释放预览URL
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // 上传所有图片
  const uploadAllImages = async (): Promise<string[]> => {
    const filesToUpload = images.filter(img => !img.uploaded && !img.error);
    if (filesToUpload.length === 0) {
      return images.filter(img => img.uploaded && img.url).map(img => img.url!);
    }

    setUploadProgress({ current: 0, total: filesToUpload.length });

    const uploadedUrls: string[] = [];

    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      if (img.uploaded && img.url) {
        uploadedUrls.push(img.url);
        continue;
      }

      if (img.error) continue;

      // 更新状态为上传中
      setImages(prev => {
        const newImages = [...prev];
        newImages[i] = { ...newImages[i], uploading: true };
        return newImages;
      });

      try {
        const result = await uploadApi.uploadImage(img.file);
        if (result.success && result.data?.url) {
          uploadedUrls.push(result.data.url);
          setImages(prev => {
            const newImages = [...prev];
            newImages[i] = { 
              ...newImages[i], 
              uploading: false, 
              uploaded: true, 
              url: result.data!.url 
            };
            return newImages;
          });
        }
      } catch (error) {
        setImages(prev => {
          const newImages = [...prev];
          newImages[i] = { 
            ...newImages[i], 
            uploading: false, 
            error: error instanceof Error ? error.message : '上传失败' 
          };
          return newImages;
        });
        throw error;
      }

      setUploadProgress(prev => ({ ...prev, current: prev.current + 1 }));
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const tagsString = formData.get('tags') as string;
    const isPublic = formData.get('isPublic') === 'on';
    const selectedCategory = formData.get('category') as string;
    const priceValue = formData.get('price') as string;
    const price = parseFloat(priceValue) || 0;
    const description = formData.get('description') as string || '';
    
    if (!selectedCategory || selectedCategory === '') {
      toast.error('请选择提示词分类');
      setIsLoading(false);
      return;
    }

    // 验证价格
    if (price < 0 || price > 1000) {
      toast.error('价格必须在0-1000元之间');
      setIsLoading(false);
      return;
    }

    // 验证付费提示词必须填写介绍
    if (price > 0 && !description.trim()) {
      toast.error('付费提示词必须填写提示词介绍');
      setIsLoading(false);
      return;
    }

    try {
      // 先上传图片
      let uploadedImageUrls: string[] = [];
      if (images.length > 0) {
        toast.loading('正在上传图片...', { id: 'upload-images' });
        uploadedImageUrls = await uploadAllImages();
        toast.success(`成功上传 ${uploadedImageUrls.length} 张图片`, { id: 'upload-images' });
      }

      const promptData = {
        title: formData.get('title') as string,
        content: formData.get('content') as string,
        category: selectedCategory,
        tags: tagsString ? tagsString.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        description: price > 0 ? description : '',
        images: uploadedImageUrls,
        isPublic: isPublic,
        price: price,
      };

      await addPrompt(promptData);
      
      // 清理预览URL
      images.forEach(img => URL.revokeObjectURL(img.preview));
      setImages([]);
      
      onClose();
    } catch (error) {
      // 错误已在context中处理
      console.error('添加提示词失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 关闭时清理
  const handleClose = () => {
    images.forEach(img => URL.revokeObjectURL(img.preview));
    setImages([]);
    setUploadProgress({ current: 0, total: 0 });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-200"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">添加新提示词</h3>
              <button
                onClick={handleClose}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title="关闭"
                aria-label="关闭表单"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">标题</label>
                <input
                  type="text"
                  name="title"
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="给您的提示词起个名字"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    分类 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    title="选择分类"
                  >
                    <option value="" disabled>请选择分类</option>
                    {PROMPT_CATEGORIES.filter(cat => cat !== '其他').map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">标签</label>
                  <input
                    type="text"
                    name="tags"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="用逗号分隔，如：写作,创意"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">内容</label>
                <textarea
                  name="content"
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="输入您的提示词内容..."
                />
              </div>

              {/* 效果图片上传区域 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <i className="fas fa-images mr-2 text-primary"></i>
                  效果展示图片 <span className="text-gray-400 font-normal">(可选，最多9张，支持拖拽上传)</span>
                </label>
                
                {/* 拖拽上传区域 */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`relative rounded-xl border-2 border-dashed p-4 transition-all ${
                    isDragging 
                      ? 'border-primary bg-primary/10 scale-[1.02]' 
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {/* 拖拽提示遮罩 */}
                  {isDragging && (
                    <div className="absolute inset-0 flex items-center justify-center bg-primary/20 rounded-xl z-10">
                      <div className="text-center">
                        <i className="fas fa-cloud-upload-alt text-4xl text-primary mb-2"></i>
                        <p className="text-primary font-medium">释放以上传图片</p>
                      </div>
                    </div>
                  )}
                  
                  {/* 图片预览网格 */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                  {images.map((img, index) => (
                    <div 
                      key={index} 
                      className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-600 group"
                    >
                      <img 
                        src={img.preview} 
                        alt={`预览 ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* 上传状态遮罩 */}
                      {img.uploading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <i className="fas fa-spinner fa-spin text-white text-2xl"></i>
                        </div>
                      )}
                      
                      {/* 上传成功标识 */}
                      {img.uploaded && (
                        <div className="absolute top-1 left-1 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                          <i className="fas fa-check text-xs"></i>
                        </div>
                      )}
                      
                      {/* 上传失败标识 */}
                      {img.error && (
                        <div className="absolute inset-0 bg-red-500 bg-opacity-50 flex items-center justify-center">
                          <i className="fas fa-exclamation-triangle text-white text-xl"></i>
                        </div>
                      )}
                      
                      {/* 删除按钮 */}
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="删除图片"
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  ))}
                  
                    {/* 添加图片按钮 */}
                    {images.length < MAX_IMAGES && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary dark:hover:border-primary transition-colors flex flex-col items-center justify-center text-gray-400 hover:text-primary"
                      >
                        <i className="fas fa-cloud-upload-alt text-2xl mb-1"></i>
                        <span className="text-xs">点击或拖拽</span>
                      </button>
                    )}
                  </div>
                  
                  {/* 隐藏的文件输入 */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    multiple
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  
                  {/* 上传进度 */}
                  {uploadProgress.total > 0 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      上传进度: {uploadProgress.current}/{uploadProgress.total}
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  支持 JPG、PNG、GIF、WebP 格式，单张最大 5MB，可拖拽上传
                </p>
              </div>

              {/* 分享选项 */}
              <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-4 bg-gray-50 dark:bg-gray-700 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">分享到提示词广场</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-300">勾选后，其他用户可以在广场看到并复制您的提示词</p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isPublic"
                      name="isPublic"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2"
                    />
                    <label htmlFor="isPublic" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      公开分享
                    </label>
                  </div>
                </div>

                {/* 价格设置 */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        <i className="fas fa-tag text-emerald-500 mr-2"></i>
                        设置价格
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-300">设置价格后，用户需付费才能查看完整内容（0表示免费）</p>
                    </div>
                    <div className="flex items-center ml-4">
                      <span className="text-gray-500 mr-2">¥</span>
                      <input
                        type="number"
                        name="price"
                        min="0"
                        max="1000"
                        step="0.01"
                        defaultValue="0"
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    <i className="fas fa-info-circle mr-1"></i>
                    价格范围：0-1000元，设置后其他用户需支付才能解锁完整内容
                  </p>
                </div>
              </div>

              {/* 提示词介绍 - 动态显示，需要JavaScript控制 */}
              <div id="description-section" className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <i className="fas fa-info-circle mr-2 text-amber-500"></i>
                  提示词介绍（付费提示词必填）
                </label>
                <textarea
                  name="description"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="简要介绍这个提示词的功能和使用场景，吸引用户购买..."
                />
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  <i className="fas fa-lightbulb mr-1"></i>
                  付费提示词在广场展示介绍内容，用户购买后才能看到完整内容
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-primary hover:bg-primary-dark text-white py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      {uploadProgress.total > 0 ? '上传中...' : '添加中...'}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus mr-2"></i>
                      添加提示词
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddPromptModal;
